// ============================================================================
// RG PRIMITIVES — thin wrappers over the semantic component classes defined in
// src/index.css (@layer components), which read --rg-* theme tokens. These give
// components a product vocabulary (shell / card / button / alert / warm surface)
// instead of raw Tailwind slate classes. Both Dark and Light are produced by the
// token foundation; primitives never hardcode final colors.
//
// Phase 1 scope: foundation proof. More variants/primitives arrive in later
// phases (status badges in Phase 2, etc.).
// ============================================================================

import React from 'react';
import { getStatusMeta, STATUS } from '../../core/statusConfig.js';

/**
 * RGPageShell — fixed full-bleed themed canvas behind the app. Flat cool app
 * background in Light, depth gradient in Dark (handled in CSS). Children render
 * decorative layers (e.g. the ambient blob).
 */
export function RGPageShell({ className = '', children }) {
  return (
    <div className={`rg-app-shell ${className}`}>
      {children}
    </div>
  );
}

/**
 * RGCard — operational surface.
 * @param {{ variant?: 'surface'|'muted'|'elevated'|'warm', as?: any, className?: string }} props
 */
export function RGCard({ variant = 'surface', as: Tag = 'div', className = '', children, ...rest }) {
  const base =
    variant === 'muted' ? 'rg-surface-muted'
    : variant === 'warm' ? 'rg-surface-warm rounded-2xl'
    : variant === 'elevated' ? 'rg-card rg-card--elevated'
    : 'rg-card';
  return (
    <Tag className={`${base} ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * RGButton — themed action button.
 * @param {{ variant?: 'primary'|'secondary', className?: string }} props
 */
export function RGButton({ variant = 'primary', type = 'button', className = '', children, ...rest }) {
  const base = variant === 'secondary' ? 'rg-button-secondary' : 'rg-button-primary';
  return (
    <button type={type} className={`${base} ${className}`} {...rest}>
      {children}
    </button>
  );
}

/**
 * RGAlertBanner — inline alert / banner.
 * @param {{ variant?: 'info'|'warning'|'danger'|'success', as?: any, className?: string }} props
 */
export function RGAlertBanner({ variant = 'info', as: Tag = 'div', className = '', children, ...rest }) {
  return (
    <Tag className={`rg-alert rg-alert--${variant} ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * RGLearningCard — warm guidance/documentation surface (ivory in Light only).
 */
export function RGLearningCard({ as: Tag = 'div', className = '', children, ...rest }) {
  return (
    <Tag className={`rg-surface-warm ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * RGStatusBadge — token-driven semantic status pill. Presentational only: it
 * reads the centralized statusConfig (no Firestore, no data mutation) and never
 * throws on unknown values (renders a neutral fallback). Works in Dark + Light.
 *
 * @param {{
 *   status: any,
 *   type?: 'outcome'|'workflow'|'priority'|'generic'|'rework',
 *   size?: 'sm'|'md',
 *   variant?: 'soft'|'outline'|'solid',
 *   showDot?: boolean,
 *   labelOverride?: string,
 *   className?: string,
 * }} props
 */
export function RGStatusBadge({
  status,
  type = STATUS.GENERIC,
  size = 'sm',
  variant = 'soft',
  showDot = false,
  labelOverride,
  className = '',
  ...rest
}) {
  const meta = getStatusMeta(status, { type });
  const sizeCls = size === 'md' ? 'px-2.5 py-1 text-xs' : 'px-2 py-0.5 text-xs';
  const variantCls =
    variant === 'outline'
      ? `bg-transparent ${meta.textClassName} ${meta.borderClassName}`
      : variant === 'solid'
      ? `${meta.bgClassName} ${meta.textClassName} ${meta.borderClassName}`
      : meta.badgeClassName; // soft (default)
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md border font-medium ${sizeCls} ${variantCls} ${className}`}
      title={meta.description || undefined}
      {...rest}
    >
      {showDot && <span className={`w-1.5 h-1.5 rounded-full ${meta.dotClassName}`} aria-hidden="true" />}
      {labelOverride ?? meta.label}
    </span>
  );
}
